import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { useRef } from "react";
import { CASE_STUDIES, DISSERTATION } from "../data";

import { Reveal, Eyebrow } from "./AmberCallout";

const CaseCard = ({ study, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <Reveal delay={index * 0.06}>
      <motion.a
        href={`#detail-${study.id}`}
        data-testid={`case-study-card-${study.id}`}
        className="group flex flex-col h-full bg-surface border border-line rounded-2xl overflow-hidden hover:border-terra/50 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra"
        whileHover={{ y: -6 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div ref={ref} className="relative h-40 sm:h-44 overflow-hidden">
          <motion.img
            src={study.image}
            alt={study.imageAlt || ""}
            aria-hidden={!study.imageAlt}
            className="w-full h-[120%] object-cover"
            style={{ y: imgY }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/25 to-transparent" />
          <span className="absolute top-4 left-4 font-mono text-xs text-parchment/90 tracking-widest">
            {study.number}
          </span>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <p className="text-xs font-mono uppercase tracking-wider text-terra mb-2">
            {study.tag} · {study.date}
          </p>
          <h3 className="font-serif text-xl font-medium text-ink leading-snug group-hover:text-terra transition-colors duration-300">
            {study.title}
          </h3>
          <p className="mt-3 text-sm text-ink2 leading-relaxed flex-1">{study.summary}</p>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-sans font-medium text-terra">
            Read the case study
            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
          </span>
        </div>
      </motion.a>
    </Reveal>
  );
};

const Work = () => (
  <section id="work" data-testid="work-section" className="py-20 sm:py-28 lg:py-32 bg-parchment2/60 scroll-mt-20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Selected Work</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink max-w-2xl leading-tight">
          Five projects, five ways of making complexity manageable.
        </h2>
        <p className="mt-5 max-w-xl text-base text-ink2 leading-relaxed">
          Each case study follows the same structure — context, the real problem, what I did, and
          what it taught me — followed by my MSc research, with honest caveats where the evidence is
          limited.
        </p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {CASE_STUDIES.map((study, i) => (
          <CaseCard key={study.id} study={study} index={i} />
        ))}
      </div>

      <Reveal delay={0.1} className="mt-6 lg:mt-8">
        <motion.a
          href={`#detail-${DISSERTATION.id}`}
          data-testid={`case-study-card-${DISSERTATION.id}`}
          className="group grid grid-cols-1 md:grid-cols-12 bg-night text-parchment rounded-2xl overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra"
          whileHover={{ y: -6 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative h-44 md:h-auto md:col-span-4 overflow-hidden">
            <img
              src={DISSERTATION.image}
              alt={DISSERTATION.imageAlt || ""}
              aria-hidden={!DISSERTATION.imageAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-night/40 to-transparent" />
          </div>
          <div className="md:col-span-8 p-6 sm:p-8 lg:p-10">
            <p className="text-xs font-mono uppercase tracking-wider text-terra mb-2">
              Featured Research · {DISSERTATION.tag}
            </p>
            <h3 className="font-serif text-xl sm:text-2xl font-medium leading-snug group-hover:text-terra transition-colors duration-300">
              {DISSERTATION.title}
            </h3>
            <p className="mt-3 text-sm sm:text-base text-parchment/70 leading-relaxed max-w-2xl">
              {DISSERTATION.summary}
            </p>
            {DISSERTATION.metrics && (
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                {DISSERTATION.metrics.map((m) => (
                  <span key={m.label} className="text-xs font-mono text-parchment/60">
                    <span className="text-terra font-medium">{m.value}</span> {m.label}
                  </span>
                ))}
              </div>
            )}
            <span className="mt-6 inline-flex items-center gap-4">
              <span className="inline-flex items-center gap-2 text-sm font-sans font-medium text-terra">
                Read the research case
                <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
              </span>
              <a
                href="/files/Irene-Chen-Dissertation-Lay-Summary.pdf"
                download="Irene-Chen-Dissertation-Lay-Summary.pdf"
                data-testid="dissertation-lay-summary-download"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-2 text-sm font-sans font-medium text-parchment/80 border border-parchment/30 rounded-full px-4 py-2 hover:border-terra hover:text-terra transition-colors duration-300"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
                Lay Summary (PDF)
              </a>
            </span>
          </div>
        </motion.a>
      </Reveal>
    </div>
  </section>
);

export default Work;
